import React from "react";

type Props = {
    page: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    pageSizeOptions?: number[];
    maxButtons?: number;
    className?: string;
};

export const PaginationBar: React.FC<Props> = ({
                                                   page,
                                                   totalItems,
                                                   pageSize,
                                                   onPageChange,
                                                   onPageSizeChange,
                                                   pageSizeOptions = [20, 50, 100],
                                                   maxButtons = 5,
                                                   className = ""
                                               }) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const clampedPage = Math.min(Math.max(1, page), totalPages);

    const goFirst = () => onPageChange(1);
    const goPrev = () => onPageChange(Math.max(1, clampedPage - 1));
    const goNext = () => onPageChange(Math.min(totalPages, clampedPage + 1));
    const goLast = () => onPageChange(totalPages);

    // Build a compact window of page numbers
    const getPageWindow = () => {
        const half = Math.floor(maxButtons / 2);
        let start = Math.max(1, clampedPage - half);
        let end = Math.min(totalPages, start + maxButtons - 1);
        // shift window if near the end
        start = Math.max(1, Math.min(start, Math.max(1, end - maxButtons + 1)));
        const pages: number[] = [];
        for (let p = start; p <= end; p++) pages.push(p);
        return pages;
    };

    const pages = getPageWindow();

    // ARIA helpers
    const isDisabled = (cond: boolean) =>
        cond ? "opacity-50 pointer-events-none" : "";

    return (
        <div className={`flex flex-col gap-3 md:flex-row md:items-center md:justify-between ${className}`}>
            {/* Page size selector */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Элементов на странице:</span>
                <div className="inline-flex rounded-xl shadow-sm overflow-hidden border">
                    {pageSizeOptions.map((size, idx) => (
                        <button
                            key={size}
                            type="button"
                            onClick={() => onPageSizeChange(size)}
                            className={`px-3 py-1.5 text-sm focus:outline-none focus:ring
                                ${pageSize === size ? "bg-gray-900 text-white" : "bg-white hover:bg-gray-100"}
                                ${idx !== pageSizeOptions.length - 1 ? "border-r" : ""}
                            `}
                            aria-pressed={pageSize === size}
                        >
                            {size}
                        </button>
                    ))}
                </div>
                <span className="ml-3 text-sm text-gray-500">
                    Всего: {totalItems}
                </span>
            </div>

            {/* Pagination controls */}
            <nav className="flex items-center gap-1" aria-label="Pagination">
                <button
                    type="button"
                    onClick={goFirst}
                    className={`px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100 ${isDisabled(clampedPage === 1)}`}
                    aria-label="Вперед"
                >
                    « Первая
                </button>
                <button
                    type="button"
                    onClick={goPrev}
                    className={`px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100 ${isDisabled(clampedPage === 1)}`}
                    aria-label="Назад"
                >
                    ‹ Назад
                </button>

                {/* Leading ellipsis */}
                {pages[0] > 1 && (
                    <>
                        <button
                            type="button"
                            onClick={() => onPageChange(1)}
                            className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100"
                        >
                            1
                        </button>
                        {pages[0] > 2 && (
                            <span className="px-2 text-gray-500 select-none">…</span>
                        )}
                    </>
                )}

                {pages.map((p) => (
                    <button
                        key={p}
                        type="button"
                        onClick={() => onPageChange(p)}
                        className={`px-3 py-1.5 rounded-lg border
                            ${p === clampedPage ? "bg-gray-900 text-white" : "bg-white hover:bg-gray-100"}`}
                        aria-current={p === clampedPage ? "page" : undefined}
                    >
                        {p}
                    </button>
                ))}

                {/* Trailing ellipsis */}
                {pages[pages.length - 1] < totalPages && (
                    <>
                        {pages[pages.length - 1] < totalPages - 1 && (
                            <span className="px-2 text-gray-500 select-none">…</span>
                        )}
                        <button
                            type="button"
                            onClick={() => onPageChange(totalPages)}
                            className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100"
                        >
                            {totalPages}
                        </button>
                    </>
                )}

                <button
                    type="button"
                    onClick={goNext}
                    className={`px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100 ${isDisabled(clampedPage === totalPages)}`}
                    aria-label="Первая"
                >
                    Вперед ›
                </button>
                <button
                    type="button"
                    onClick={goLast}
                    className={`px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100 ${isDisabled(clampedPage === totalPages)}`}
                    aria-label="Последняя"
                >
                    Последняя »
                </button>
            </nav>
        </div>
    );
};
